"use client";

import { Card, IconButton, InputAdornment } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { useFormikContext } from "formik";

import FormField from "@/components/atoms/FormField";
import MDBox from "@/components/atoms/MDBox";
import { MDButton } from "@/components/atoms/MDButton";
import MDTypography from "@/components/atoms/MDTypography";
import {
  checkIfHasLoweCase,
  checkIfHasNumbers,
  checkIfHasSpecialCharacters,
  checkIfHasUpperCase,
} from "@/helpers/string-helpers";
import useShowPassword from "@/hooks/use-show-password";
import { ProfileMessages } from "@/messages/profile/profile.messages";

import { changePasswordFormNames } from "./form-data";
import { ChangePasswordFormData } from "./types";

type ChangePasswordFormProps = {
  messages: ProfileMessages;
  isLoading: boolean;
};

export default function ChangePasswordForm({ messages, isLoading }: ChangePasswordFormProps) {
  const { values, errors, submitForm, handleChange } = useFormikContext<ChangePasswordFormData>();

  const visibilityInitialValue = true;

  const {
    handlePasswordVisibility: handlePasswordVisibilityOld,
    inputType: inputTypeOld,
    inputIcon: inputIconOld,
  } = useShowPassword({ initialValue: visibilityInitialValue });
  const {
    handlePasswordVisibility: handlePasswordVisibilityNew,
    inputType: inputTypeNew,
    inputIcon: inputIconNew,
  } = useShowPassword({ initialValue: visibilityInitialValue });
  const {
    handlePasswordVisibility: handlePasswordVisibilityConfirm,
    inputType: inputTypeConfirm,
    inputIcon: inputIconConfirm,
  } = useShowPassword({ initialValue: visibilityInitialValue });

  const MIN_CHARACTERS = 8;
  const hasSpecialChar = checkIfHasSpecialCharacters(values.newPassword);
  const hasUpperCase = checkIfHasUpperCase(values.newPassword);
  const hasLowerCase = checkIfHasLoweCase(values.newPassword);
  const hasNumber = checkIfHasNumbers(values.newPassword);
  const hasMinCharacters = values.newPassword.length >= MIN_CHARACTERS;

  const enableBtn = hasSpecialChar && hasUpperCase && hasLowerCase && hasNumber && hasMinCharacters;

  const passwordRequirements = [
    { label: messages.changePasswordSpecialCharacterRule, isValid: hasSpecialChar },
    { label: messages.changePasswordLowerRule, isValid: hasLowerCase },
    { label: messages.changePasswordUpperRule, isValid: hasUpperCase },
    { label: messages.changePasswordOneNumberRule, isValid: hasNumber },
    { label: messages.changePasswordMinRule, isValid: hasMinCharacters },
  ];

  const renderPasswordRequirements = passwordRequirements.map((requirement, index) => (
    <MDBox
      key={index}
      component="li"
      color={requirement.isValid ? "success" : "warning"}
      fontSize="1.25rem"
      lineHeight={1}
    >
      <MDTypography variant="button" color="text" fontWeight="regular" verticalAlign="middle">
        {requirement.label}
      </MDTypography>
    </MDBox>
  ));

  return (
    <Card>
      <MDBox p={3}>
        <MDTypography variant="h5">{messages.changePassworTitle}</MDTypography>
      </MDBox>
      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3}>
          <Grid size={12}>
            <FormField
              placeholder={messages.oldPasswordPlaceholder}
              fullWidth
              disableIcon
              type={inputTypeOld}
              InputLabelProps={{ shrink: true }}
              label={messages.oldPasswordLabel}
              name={changePasswordFormNames.oldPassword}
              onChange={handleChange}
              value={values.oldPassword}
              error={!!errors.oldPassword}
              helperText={errors.oldPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handlePasswordVisibilityOld}>{inputIconOld}</IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={12}>
            <FormField
              placeholder={messages.newPasswordPlaceholder}
              fullWidth
              type={inputTypeNew}
              InputLabelProps={{ shrink: true }}
              label={messages.newPasswordLabel}
              name={changePasswordFormNames.newPassword}
              onChange={handleChange}
              value={values.newPassword}
              disableIcon
              error={!!errors.newPassword}
              helperText={errors.newPassword}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handlePasswordVisibilityNew}>{inputIconNew}</IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid size={12}>
            <FormField
              placeholder={messages.confirmPasswordPlaceholder}
              fullWidth
              type={inputTypeConfirm}
              InputLabelProps={{ shrink: true }}
              label={messages.confirmPasswordLabel}
              name={changePasswordFormNames.confirmPassword}
              onChange={handleChange}
              value={values.confirmPassword}
              error={!!errors.confirmPassword}
              helperText={errors.confirmPassword}
              disableIcon={true}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={handlePasswordVisibilityConfirm}>
                      {inputIconConfirm}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
        <MDBox mt={6} mb={1}>
          <MDTypography variant="h5">{messages.changePasswordRequirements}</MDTypography>
        </MDBox>
        <MDBox mb={1}>
          <MDTypography variant="body2" color="text">
            {messages.changePasswordRequirementsDescription}
          </MDTypography>
        </MDBox>
        <MDBox display="flex" justifyContent="space-between" alignItems="flex-end" flexWrap="wrap">
          <MDBox component="ul" m={0} pl={3.25} mb={{ xs: 8, sm: 0 }}>
            {renderPasswordRequirements}
          </MDBox>
          <MDBox ml="auto">
            <MDButton
              variant="gradient"
              color="dark"
              onClick={submitForm}
              disabled={!enableBtn}
              isLoading={isLoading}
            >
              {messages.updateButton}
            </MDButton>
          </MDBox>
        </MDBox>
      </MDBox>
    </Card>
  );
}
